<template>
    <div class="side-bar-container">
        <div class="side-bar">
            <div class="side-bar-logo">
                <router-link to="/" class="side-bar-title">
                    <img v-if="platformLogo" :src="platformLogo" class="side-bar-logo-image" :alt="$t('Platform logo')" />
                    <img v-else src="@/assets/logos/f-air-blanco.svg" class="side-bar-logo-image" :alt="$t('Platform logo')" />
                </router-link>
                <router-link to="/" class="side-bar-title-compact">
                    <img
                        v-if="platformLogoCompact || platformLogo"
                        :src="platformLogoCompact || platformLogo"
                        class="side-bar-logo-image"
                        :alt="$t('Platform logo')"
                    />
                    <img v-else src="@/assets/logos/imagotipo-blanco.svg" class="side-bar-logo-image" :alt="$t('Platform logo')" />
                </router-link>
            </div>
            <div class="side-bar-body">
                <router-link
                    v-if="homeSection.visible"
                    class="side-bar-option"
                    :class="{ selected: activeSectionId === 'home' }"
                    :title="homeSection.title"
                    :to="{ name: homeSection.route }"
                >
                    <div class="side-bar-option-icon"><i :class="homeSection.icon"></i></div>
                    <div class="side-bar-option-text">{{ homeSection.title }}</div>
                </router-link>

                <AccordionComponent
                    v-for="section in sidebarAccordionSections"
                    :key="section.id"
                    :title="section.title"
                    :icon="section.icon"
                    :items="section.items || []"
                    :expanded="isAccordionExpanded(section.id)"
                    @update:expanded="onAccordionExpanded(section.id, $event)"
                    :is-collapsed="!expandedStatus"
                    :current-page="page"
                />

                <router-link
                    v-if="clearingHouseSection.visible"
                    class="side-bar-option"
                    :class="{ selected: activeSectionId === 'clearing-house' }"
                    :title="clearingHouseSection.title"
                    :to="{ name: clearingHouseSection.route }"
                >
                    <div class="side-bar-option-icon"><i :class="clearingHouseSection.icon"></i></div>
                    <div class="side-bar-option-text">{{ clearingHouseSection.title }}</div>
                </router-link>
            </div>
            <div class="side-bar-powered-by" v-if="platformLogo">
                <div class="side-bar-powered-by-text">{{ $t("Powered by") }}</div>
                <router-link to="/" class="logo-powered-by">
                    <img src="@/assets/logos/f-air-blanco.svg" class="side-bar-logo-image" :alt="$t('Powered by logo')" />
                </router-link>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "../../utils/v-model";
import { AuthController } from "@/control/auth";
import AccordionComponent from "../ui/Accordion.vue";
import {
    buildSidebarNavigation,
    getActiveSidebarSection,
    getSidebarNavigationSections,
    getSidebarSectionById,
    isSidebarAccordionSectionId,
} from "@/utils/sidebar-navigation";
import type { SidebarAccordionSectionId, SidebarNavigationSection, SidebarSectionId } from "@/utils/sidebar-navigation";

export default defineComponent({
    components: {
        AccordionComponent,
    },
    name: "MenuSideBar",
    emits: ["update:expanded"],
    props: {
        expanded: Boolean,
    },
    setup(props) {
        return {
            expandedStatus: useVModel(props, "expanded"),
        };
    },
    data() {
        return {
            page: "",
            expanding: false,
            navigationVersion: 0,
            expandedAccordion: null as SidebarAccordionSectionId | null,
            platformLogo: AuthController.PlatformLogo,
            platformLogoCompact: AuthController.PlatformLogoCompact,
        };
    },
    computed: {
        navigationSections: function (): SidebarNavigationSection[] {
            void this.navigationVersion;

            return buildSidebarNavigation({
                $t: (key: string) => this.$t(key),
                isAuthenticated: AuthController.isAuthenticated(),
                hasPermission: (permission: string) => AuthController.hasPermission(permission),
                clearingHouseEnabled: AuthController.ClearingHouseEnabled,
            });
        },
        sidebarNavigationSections: function (): SidebarNavigationSection[] {
            return getSidebarNavigationSections(this.navigationSections);
        },
        sidebarAccordionSections: function (): SidebarNavigationSection[] {
            return this.sidebarNavigationSections.filter((section) => isSidebarAccordionSectionId(section.id));
        },
        activeSidebarSection: function (): SidebarNavigationSection | null {
            return getActiveSidebarSection(this.page, this.sidebarNavigationSections);
        },
        activeSectionId: function (): SidebarSectionId | "" {
            return this.activeSidebarSection?.id || "";
        },
        homeSection: function (): SidebarNavigationSection {
            return this.getNavigationSection("home");
        },
        clearingHouseSection: function (): SidebarNavigationSection {
            return this.getNavigationSection("clearing-house");
        },
    },
    methods: {
        updatePage: function () {
            this.page = this.$route?.name ? String(this.$route.name) : "";
            this.syncExpandedAccordionWithPage();
        },

        getNavigationSection: function (sectionId: SidebarSectionId): SidebarNavigationSection {
            const section = getSidebarSectionById(this.sidebarNavigationSections, sectionId);
            if (section) return section;

            return {
                id: sectionId,
                title: "",
                icon: "",
                visible: false,
                placements: ["sidebar"],
                items: [],
            };
        },

        getAccordionForPage: function (page: string): SidebarAccordionSectionId | null {
            const activeSection = getActiveSidebarSection(page, this.sidebarNavigationSections);

            return activeSection && isSidebarAccordionSectionId(activeSection.id) ? activeSection.id : null;
        },

        setExpandedAccordion: function (accordion: SidebarAccordionSectionId | null) {
            this.expandedAccordion = accordion;
        },

        isAccordionExpanded: function (accordion: SidebarSectionId): boolean {
            return this.expandedAccordion === accordion;
        },

        syncExpandedAccordionWithPage: function () {
            this.setExpandedAccordion(this.getAccordionForPage(this.page));
        },

        onAccordionExpanded: function (accordion: SidebarSectionId, expanded: boolean) {
            if (!isSidebarAccordionSectionId(accordion)) return;

            if (expanded) {
                this.setExpandedAccordion(accordion);
                return;
            }

            if (this.expandedAccordion === accordion) {
                this.setExpandedAccordion(null);
            }
        },

        onAuthChanged: function () {
            this.platformLogo = AuthController.PlatformLogo;
            this.platformLogoCompact = AuthController.PlatformLogoCompact;
            this.navigationVersion += 1;
            this.syncExpandedAccordionWithPage();
        },
    },
    mounted: function () {
        this.updatePage();

        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
    },
    watch: {
        $route: function () {
            this.updatePage();
        },
        expanded: function () {
            this.expanding = true;
            setTimeout(() => {
                this.expanding = false;
            }, 200);
        },
    },
});
</script>
